import { ICompanyRepository } from '@domain/account/ICompanyRepository';
import { Company, CompanySettings } from '@domain/account/models/Company';
import { CompanyDataSource } from '../datasources/CompanyDataSource';
import { Resource } from '@domain/base/Resource';
import { CompanyMapper } from '../mappers/CompanyMapper';
import { Membership } from '@domain/account/models/Membership';
import { MembershipMapper } from '../mappers/MembershipMapper';

export class CompanyRepository implements ICompanyRepository {
    constructor(private dataSource: CompanyDataSource) { }

    async getCompanies(page?: number, limit?: number, search?: string, subscriptionStatuses?: string[]): Promise<Resource<Company[]>> {
        try {
            const companies = await this.dataSource.getCompanies(page, limit, search, subscriptionStatuses);
            return {
                status: 'success',
                data: companies.map(CompanyMapper.toModel),
            };
        } catch (error) {
            return {
                status: 'error',
                error: {
                    type: 'UnknownError',
                    message: 'Failed to get companies'
                }
            };
        }
    }

    async getCompany(id: string): Promise<Resource<Company>> {
        try {
            const company = await this.dataSource.getCompany(id);
            return {
                status: 'success',
                data: CompanyMapper.toModel(company)
            };
        } catch (error) {
            return {
                status: 'error',
                error: {
                    type: 'UnknownError',
                    message: 'Failed to get company'
                }
            };
        }
    }

    async getUserMemberships(): Promise<Resource<Membership[]>> {
        try {
            const memberships = await this.dataSource.getUserMemberships();
            return {
                status: 'success',
                data: memberships.map(MembershipMapper.toModel)
            };
        } catch (error) {
            return {
                status: 'error',
                error: {
                    type: 'UnknownError',
                    message: 'Failed to get user memberships'
                }
            };
        }
    }

    async getSettings(): Promise<Resource<CompanySettings>> {
        try {
            const settings = await this.dataSource.getSettings();
            return { status: 'success', data: settings };
        } catch (error) {
            return {
                status: 'error',
                error: {
                    type: 'UnknownError',
                    message: 'Failed to get company settings'
                }
            };
        }
    }

    async updateSettings(settings: Partial<CompanySettings>): Promise<Resource<CompanySettings>> {
        try {
            const updated = await this.dataSource.updateSettings(settings);
            return { status: 'success', data: updated };
        } catch (error) {
            return {
                status: 'error',
                error: {
                    type: 'UnknownError',
                    message: 'Failed to update company settings'
                }
            };
        }
    }
} 